import { Component, OnChanges, Input, ViewChildren, QueryList } from '@angular/core';
import { ProfileEditBillingComponent } from 'src/app/profiles/profile-edit';
import { ProfileService } from 'src/app/profiles/shared/profile.service';
import { ArbService } from 'src/app/revenue/shared/billing/arb.service';
import { Billing } from 'src/app/revenue/shared/billing/billing.model';
import { BillingService } from 'src/app/revenue/shared/billing/billing.service';
import { ArbCommandResultDto } from 'src/app/revenue/shared/billing/dto/arb-command-result.dto';
import { AuthService, NotificationService } from 'src/app/shared';
import {
  CreateObjectValueAccessorProvider,
  CreateObjectValueValidationProvider,
  ObjectValueAccessor,
} from 'src/app/shared/widgets/value-accessors/object-value-accessor';
import { PledgeEditBillingComponent } from './pledge-edit-billing.component';
import { BillingSearch } from 'src/app/revenue/shared/billing/billing-search.model';
import { BillingCycleName, PledgeDto } from 'src/app/revenue/shared/pledge/dto/pledge.dto';
import { UserService } from 'src/app/users/shared/user.service';
import { UserRead } from 'src/app/users/shared/user-read.model';
import { RevenueItemService } from 'src/app/revenue/shared/revenue-items/revenue-item.service';
import { SelectItem } from 'primeng/api';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';

@Component({
  selector: 'wl-pledge-edit-billing-list',
  templateUrl: 'pledge-edit-billing-list.component.html',
  providers: [
    CreateObjectValueAccessorProvider(PledgeEditBillingListComponent),
    CreateObjectValueValidationProvider(PledgeEditBillingListComponent),
  ],
    standalone: false
})
export class PledgeEditBillingListComponent extends ObjectValueAccessor<Array<Billing>> implements OnChanges {
  @Input() pledge: PledgeDto;
  @Input() profileId: number;
  @ViewChildren(PledgeEditBillingComponent) _billingComponents: QueryList<PledgeEditBillingComponent>;

  showInactive = false;
  loading = false;
  currentUserId: number;
  revItems: SelectItem[] = [];

  public constructor(
    private _billingService: BillingService,
    private _arbService: ArbService,
    private _notificationService: NotificationService,
    private _authService: AuthService,
    private _revenueItemService: RevenueItemService
  ) {
    super([], false);
  }

  ngOnChanges() {
    this.loadBillings();
    this._authService.getUserInfo().then((user) => (this.currentUserId = Number(user.wli_usernum ?? 0)));
    this._revenueItemService
      .getAllRevenueItems()
      .then((data) => {
        if (data.length > 0) {
          this.revItems = data.map<SelectItem>((x: RevenueItem) => {
            return { label: x.ItemName, value: x.ItemNum };
          });
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
  }

  addBilling() {
    const billing = {} as Billing;
    billing.AnchorDay = this.pledge.StartDate.getDate();
    billing.AnchorMonth = this.pledge.StartDate.getMonth() + 1;
    billing.StartingYear = this.pledge.StartDate.getFullYear();
    billing.BillingCycle = this.pledge.BillingCycle;
    billing.BillingExpireDate = this.pledge.EndDate;
    billing.BillingExpires = true;
    billing.PledgeID = this.pledge.PledgeId;
    billing.ProfileId = this.pledge.ProfileId;
    billing.Quantity = 1;
    billing.SoldBy = this.currentUserId;

    if (this.pledge.ItemNum && this.pledge.ItemNum > 0) {
      billing.ItemNum = this.pledge.ItemNum;
      billing.RevenueItem = this.revItems.find((ri) => ri.value === this.pledge.ItemNum).label;
    } else {
      billing.ItemNum = this.revItems[0].value;
      billing.RevenueItem = this.revItems[0].label;
    }

    if (this.pledge.BillingCycle && this.pledge.BillingCycle !== '') {
      billing.AmountPerCycle = this.calculateAmountPerCycle(this.pledge);
      billing.UnitPrice = this.calculateAmountPerCycle(this.pledge);
      billing.YearlyAmount = this.calculateYearlyAmount(this.pledge);
    }

    this.value.push(billing);
  }

  deleteBilling(index: number) {
    this.value.splice(index, 1);
  }

  save(): Promise<void> {
    const unchangedBillings: Array<Billing> = JSON.parse(this.pristineValue);
    const deletedBillings: Array<Billing> = unchangedBillings.filter((x) => !this.value.some((a) => x.BillingId == a.BillingId));
    const billingPromises: Array<Promise<void>> = [];
    const arbPromises: Array<Promise<ArbCommandResultDto>> = [];

    this._billingComponents.forEach((component) => {
      billingPromises.push(component.save(this.pledge.ProfileId, this.pledge.PledgeId));
    });

    for (const billing of deletedBillings) {
      if (billing.IsARB && billing.ARBID && billing.ARBID > 0) {
        arbPromises.push(this._arbService.cancelArbByBillingId(billing.BillingId));
      }
      billingPromises.push(this._billingService.deleteBilling(billing.BillingId));
    }

    if (arbPromises.length > 0) {
      return Promise.all(arbPromises).then(() => {
        return Promise.all(billingPromises).then(() => {
          this.setPristine();
        });
      });
    } else {
      return Promise.all(billingPromises).then(() => {
        this.setPristine();
      });
    }
  }

  loadBillings() {
    if (this.pledge && this.pledge.PledgeId) {
      this.loading = true;
      this._billingService
        .getBillingsForPledge(this.pledge.PledgeId)
        .then((data) => {
          this.value = data.Result;
          this.setPristine();
        })
        .catch((err) => {
          this._notificationService.httpError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  private calculateAmountPerCycle(pledge: PledgeDto) {
    let numMonths = pledge.PledgeYears * 12;
    let monthlyPayment = pledge.PledgeTotal / numMonths;
    let amountPerCycle: number;
    switch (pledge.BillingCycle) {
      case BillingCycleName.EveryMonth:
        amountPerCycle = monthlyPayment;
        break;
      case BillingCycleName.Every3Months:
        amountPerCycle = monthlyPayment * 3;
        break;
      case BillingCycleName.Every6Months:
        amountPerCycle = monthlyPayment * 6;
        break;
      case BillingCycleName.Every12Months:
        amountPerCycle = monthlyPayment * 12;
        break;
    }
    return Number(amountPerCycle.toFixed(2));
  }

  private calculateYearlyAmount(pledge: PledgeDto) {
    let amountPerCycle: number = this.calculateAmountPerCycle(pledge);
    let yearlyAmount: number;
    switch (pledge.BillingCycle) {
      case BillingCycleName.EveryMonth:
        yearlyAmount = amountPerCycle * 12;
        break;
      case BillingCycleName.Every3Months:
        yearlyAmount = amountPerCycle * 4;
        break;
      case BillingCycleName.Every6Months:
        yearlyAmount = amountPerCycle * 2;
        break;
      case BillingCycleName.Every12Months:
        yearlyAmount = amountPerCycle;
        break;
    }
    return Number(yearlyAmount.toFixed(2));
  }
}
